import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { getComplianceRules, createComplianceRule, deleteComplianceRule } from '@/lib/api';
import { Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CompliancePage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '', description: '', rule_type: 'document_required',
    document_type: 'coi', field_name: '', operator: 'exists',
    threshold_value: '', severity: 'high', is_active: true
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getComplianceRules();
      setRules(data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      await createComplianceRule(newRule);
      toast.success('Rule created');
      setShowCreate(false);
      load();
    } catch { toast.error('Failed to create rule'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteComplianceRule(id);
      toast.success('Rule deleted');
      load();
    } catch { toast.error('Failed to delete rule'); }
  };

  const severityColor = { high: 'destructive', medium: 'secondary', low: 'outline' };

  return (
    <div data-testid="compliance-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Compliance Rules</h1>
          <p className="text-sm text-muted-foreground">Configure vendor compliance requirements</p>
        </div>
        <Button data-testid="rules-create-button" onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Rule
        </Button>
      </div>

      <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : rules.length === 0 ? (
            <div className="py-16 text-center">
              <ShieldCheck className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">No rules configured</p>
            </div>
          ) : (
            <Table data-testid="rules-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Rule Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(rule => (
                  <TableRow key={rule.id} className="hover:bg-muted/50 transition-colors duration-150">
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{rule.name}</p>
                        <p className="text-xs text-muted-foreground">{rule.description}</p>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{rule.rule_type?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-sm uppercase">{rule.document_type || '-'}</TableCell>
                    <TableCell><Badge variant={severityColor[rule.severity] || 'outline'} className="text-xs capitalize">{rule.severity}</Badge></TableCell>
                    <TableCell>
                      <div className={`h-2 w-2 rounded-full ${rule.is_active ? 'bg-[hsl(142,56%,36%)]' : 'bg-muted-foreground/30'}`} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(rule.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog data-testid="rules-editor-dialog" open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Create Compliance Rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Rule Name *</Label><Input value={newRule.name} onChange={e => setNewRule(p => ({...p, name: e.target.value}))} /></div>
            <div><Label>Description</Label><Textarea value={newRule.description} onChange={e => setNewRule(p => ({...p, description: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rule Type</Label>
                <Select value={newRule.rule_type} onValueChange={v => setNewRule(p => ({...p, rule_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document_required">Document Required</SelectItem>
                    <SelectItem value="coverage_minimum">Coverage Minimum</SelectItem>
                    <SelectItem value="expiration_check">Expiration Check</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document Type</Label>
                <Select value={newRule.document_type} onValueChange={v => setNewRule(p => ({...p, document_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['coi','w9','license','workers_comp','auto_insurance','umbrella','bond'].map(t =>
                      <SelectItem key={t} value={t}>{t.toUpperCase().replace('_', ' ')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Severity</Label>
                <Select value={newRule.severity} onValueChange={v => setNewRule(p => ({...p, severity: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Operator</Label>
                <Select value={newRule.operator} onValueChange={v => setNewRule(p => ({...p, operator: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exists">Exists</SelectItem>
                    <SelectItem value="gte">Greater/Equal</SelectItem>
                    <SelectItem value="lte">Less/Equal</SelectItem>
                    <SelectItem value="not_expired">Not Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newRule.rule_type === 'coverage_minimum' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Field Name</Label><Input placeholder="e.g. general_liability" value={newRule.field_name} onChange={e => setNewRule(p => ({...p, field_name: e.target.value}))} /></div>
                <div><Label>Threshold</Label><Input placeholder="e.g. 1000000" value={newRule.threshold_value} onChange={e => setNewRule(p => ({...p, threshold_value: e.target.value}))} /></div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={newRule.is_active} onCheckedChange={v => setNewRule(p => ({...p, is_active: v}))} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="rules-save-button" onClick={handleCreate} disabled={!newRule.name}>Save Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
